import { motion, type Transition, type Variants } from 'framer-motion';
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { ScrollToBottom } from 'use-stick-to-bottom';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';

export interface CollapsibleProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'onClick' | 'id'> {
  title: React.ReactNode;
  titleClassName?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  toggleButton?: boolean;
  toggleButtonPosition?: 'left' | 'right';
  toggleButtonClassName?: string;
  actionButtons?: ReactNode;
  onClick?(): void;
  onToggle?(open: boolean): void;
  scrollToBottom?: ScrollToBottom;
}

const variants = {
  closed: {
    height: 0,
    visibility: 'hidden',
  },
  open: {
    height: 'auto',
    visibility: 'visible',
  },
} satisfies Variants;

const transition = { duration: 0.2, ease: cubicEasingFn } satisfies Transition;

export function Collapsible(originalProps: CollapsibleProps) {
  const {
    title,
    open,
    titleClassName,
    children,
    toggleButton = true,
    toggleButtonPosition = 'right',
    toggleButtonClassName,
    className,
    actionButtons,
    onClick,
    onToggle,
    scrollToBottom,
    ...props
  } = originalProps;

  const initialRender = useRef(true);
  const id = `bolt-collapsible-${useId()}`;

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    if (open) {
      scrollToBottom?.({
        animation: { mass: 0.9 },
        duration: 200,
        preserveScrollPosition: true,
      });
    }
  }, [open]);

  if (open == null) {
    return <UncontrolledCollapsible {...originalProps} />;
  }

  const Title = onClick ? 'button' : 'div';

  return (
    <div
      className={classNames(
        'collapsible flex flex-col border border-bolt-elements-borderColor grid rounded-lg w-full transition-border duration-150',
        className,
        {
          'rounded-t-lg': open,
          'rounded-lg': !open,
        },
      )}
      {...props}
    >
      <div
        className={classNames('flex relative', {
          'flex-row-reverse': toggleButtonPosition === 'left',
        })}
      >
        <Title
          className={classNames(
            'flex text-left items-stretch w-full overflow-hidden',
            {
              'rounded-tr-lg': open && toggleButtonPosition === 'left',
              'rounded-r-lg': !open && toggleButtonPosition === 'left',
              'rounded-tl-lg': open && toggleButtonPosition === 'right',
              'rounded-l-lg': !open && toggleButtonPosition === 'right',
            },
            titleClassName,
          )}
          onClick={onClick}
        >
          {title}
          {actionButtons}
        </Title>

        {toggleButton && (
          <div
            className={classNames('flex-1 flex items-center', toggleButtonClassName, {
              'px-2': toggleButtonPosition === 'left',
              'pr-2': toggleButtonPosition === 'right',
              'rounded-tl-lg': open && toggleButtonPosition === 'left',
              'rounded-l-lg': !open && toggleButtonPosition === 'left',
              'rounded-tr-lg': open && toggleButtonPosition === 'right',
              'rounded-r-lg': !open && toggleButtonPosition === 'right',
            })}
          >
            <LegacyIconButton
              icon={open ? 'i-ph:caret-up' : 'i-ph:caret-down'}
              size="lg"
              onClick={() => onToggle?.(!open)}
              aria-expanded={open}
              aria-controls={id}
              aria-label={open ? 'Close' : 'Show'}
            />
          </div>
        )}
      </div>

      <motion.div
        id={id}
        initial="closed"
        animate={open ? 'open' : 'closed'}
        exit="closed"
        variants={variants}
        transition={transition}
        className="overflow-hidden"
      >
        <div className="bg-bolt-elements-artifacts-borderColor h-[1px]" />

        {children}
      </motion.div>
    </div>
  );
}

export function UncontrolledCollapsible({ defaultOpen = false, onToggle, ...props }: CollapsibleProps) {
  const [open, setOpen] = useState(() => defaultOpen);

  const toggle = useCallback(
    (toggled: boolean) => {
      setOpen(toggled);
      onToggle?.(toggled);
    },
    [onToggle],
  );

  return <Collapsible {...props} open={open} onToggle={toggle} />;
}
